


import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import apiClient from '../../../api/client';
import UpgradePrompt from '../../subscription/UpgradePrompt';
import './ProductInsightsList.css';
import './analytics-shared.css';

// Plain-language helpers
function getPriorityLabel(classification) {
  if (classification === 'A') return { label: '⭐ Top seller',    color: 'priority-a' };
  if (classification === 'B') return { label: '📦 Mid-range',     color: 'priority-b' };
  return                              { label: '🔹 Slow mover',   color: 'priority-c' };
}

function getSalesDirectionLabel(trend, trendPct) {
  const pct = parseFloat(trendPct).toFixed(0);
  if (trend === 'up')     return { label: `📈 Selling more (+${pct}%)`,  color: 'trend-up'     };
  if (trend === 'down')   return { label: `📉 Selling less (${pct}%)`,   color: 'trend-down'   };
  return                          { label: `➡️ Steady`,                  color: 'trend-stable' };
}

function getRestockLabel(insight) {
  if (!insight.recommended_reorder_level) return '—';
  const confidence = parseFloat(insight.confidence_score);
  const units = insight.recommended_reorder_level;
  if (confidence >= 80) return `Reorder ${units} units`;
  if (confidence >= 60) return `~${units} units (estimate)`;
  return `~${units} units (low data)`;
}

function getEarningsLabel(revenue, revenueContribution) {
  const pct  = parseFloat(revenueContribution).toFixed(1);
  const rev  = parseFloat(revenue);
  if (rev === 0) return { text: 'No sales yet', sub: '0% of revenue' };
  return { text: `TSh ${rev.toLocaleString()}`, sub: `${pct}% of all revenue` };
}

function ProductInsightsList() {
  const navigate  = useNavigate();
  const { hasFeature } = useFeatureAccess();
  const [insights, setInsights]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const [sortBy, setSortBy]       = useState('revenue');
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  useEffect(() => { loadInsights(); }, []);


  async function loadInsights() {
    try {
      const response = await apiClient.get('/product-insights/');
      const data = Array.isArray(response.data)
        ? response.data
        : (response.data.results || []);
      setInsights(data);
    } catch (error) {
      console.error('Failed to load insights:', error);
    }
    setLoading(false);
  }

  function getFilteredInsights() {
    let filtered = [...insights];
    if (filter !== 'all') {
      filtered = filtered.filter(i => i.classification === filter);
    }
    filtered.sort((a, b) => {
      if (sortBy === 'revenue') return parseFloat(b.revenue_contribution) - parseFloat(a.revenue_contribution);
      if (sortBy === 'trend')   return ['up','stable','down'].indexOf(a.trend) - ['up','stable','down'].indexOf(b.trend);
      return a.product_name.localeCompare(b.product_name);
    });
    return filtered;
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading product insights...</p>
      </div>
    );
  }

  if (!hasFeature('abc_classification')) {
    return (
      <>
        <div className="locked-screen">
          <Package size={48} />
          <h2>Product Insights</h2>
          <p>Upgrade to access detailed product analytics</p>
          <button onClick={() => setShowUpgradePrompt(true)} className="btn-primary">
            Upgrade Now
          </button>
        </div>
        {showUpgradePrompt && (
          <UpgradePrompt 
            feature="abc_classification" 
            onClose={() => setShowUpgradePrompt(false)} 
          />
        )}
      </>
    );
  }

  if (insights.length === 0) {
    return (
      <div className="product-insights-list fade-in">
        <div className="insights-header">
          <div>
            <h1>Product Performance</h1>
            <p className="subtitle">Which products are making you money — and which aren't</p>
          </div>
          <button onClick={() => navigate('/analytics')} className="btn-secondary">
            ← Analytics review
          </button>
        </div>
        <div className="empty-state">
          <Package size={48} />
          <p className="empty-title">No data yet</p>
          <p className="empty-desc">
            Go to <strong>Analytics</strong> and click <strong>Run Analytics</strong> to analyse your products.
          </p>
          <button onClick={() => navigate('/analytics')} className="btn-primary">Go to Analytics</button>
        </div>
      </div>
    );
  }

  const filteredInsights = getFilteredInsights();
  const lastCalculated   = insights[0]?.last_calculated
    ? new Date(insights[0].last_calculated).toLocaleString()
    : null;

  const counts = {
    A: insights.filter(i => i.classification === 'A').length,
    B: insights.filter(i => i.classification === 'B').length,
    C: insights.filter(i => i.classification === 'C').length,
  };

  return (
    <div className="product-insights-list fade-in">
      <div className="insights-header">
        <div>
          <h1>Product Performance</h1>
          <p className="subtitle">Which products are making you money — and which aren't</p>
          {lastCalculated && (
            <p className="last-calculated">Last updated: {lastCalculated}</p>
          )}
        </div>
        <button onClick={() => navigate('/analytics')} className="btn-secondary">
          ← Analytics full review
        </button>
      </div>

      {/* Legend */}
      <div className="priority-legend">
        <div className="legend-item legend-a">
          <span>⭐ Top sellers ({counts.A})</span>
          <span className="legend-desc">Generating ~80% of your revenue — protect stock levels</span>
        </div>
        <div className="legend-item legend-b">
          <span>📦 Mid-range ({counts.B})</span>
          <span className="legend-desc">Solid performers — worth keeping stocked</span>
        </div>
        <div className="legend-item legend-c">
          <span>🔹 Slow movers ({counts.C})</span>
          <span className="legend-desc">Low revenue contribution — review or promote these</span>
        </div>
      </div>

      {/* Filters */}
      <div className="insights-controls">
        <div className="filter-tabs">
          <button className={filter === 'all' ? 'tab active' : 'tab'} onClick={() => setFilter('all')}>
            All ({insights.length})
          </button>
          <button className={filter === 'A' ? 'tab active tab-a' : 'tab'} onClick={() => setFilter('A')}>
            ⭐ Top sellers ({counts.A})
          </button>
          <button className={filter === 'B' ? 'tab active tab-b' : 'tab'} onClick={() => setFilter('B')}>
            📦 Mid-range ({counts.B})
          </button>
          <button className={filter === 'C' ? 'tab active tab-c' : 'tab'} onClick={() => setFilter('C')}>
            🔹 Slow movers ({counts.C})
          </button>
        </div>

        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="sort-select">
          <option value="revenue">Sort by earnings</option>
          <option value="trend">Sort by sales direction</option>
          <option value="name">Sort by name</option>
        </select>
      </div>

      {/* Table */}
      <div className="insights-table-container">
        <table className="insights-table product-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>Priority</th>
              <th>Your earnings from it</th>
              <th>Sales this month vs last</th>
              {hasFeature('predictive_recommendations') && <th>Suggested restock</th>}
            </tr>
          </thead>
          <tbody>
            {filteredInsights.map(insight => {
              const priority  = getPriorityLabel(insight.classification);
              const direction = getSalesDirectionLabel(insight.trend, insight.trend_percentage);
              const earnings  = getEarningsLabel(insight.total_revenue_30d, insight.revenue_contribution);
              const restock   = getRestockLabel(insight);

              return (
                <tr key={insight.id}>
                  <td className="product-name-cell">
                    <div className="product-name">{insight.product_name}</div>
                    <div className="product-sku">{insight.product_sku}</div>
                  </td>
                  <td>
                    <span className={`priority-badge ${priority.color}`}>
                      {priority.label}
                    </span>
                  </td>
                  <td className="earnings-cell">
                    <div className="earnings-main">{earnings.text}</div>
                    <div className="earnings-sub">{earnings.sub}</div>
                  </td>
                  <td>
                    <span className={`direction-badge ${direction.color}`}>
                      {direction.label}
                    </span>
                  </td>
                  {hasFeature('predictive_recommendations') && (
                    <td className="restock-cell">
                      {restock !== '—'
                        ? <span className="restock-suggestion">💡 {restock}</span>
                        : <span className="no-data">Not enough sales data</span>
                      }
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredInsights.length === 0 && (
        <div className="empty-state">
          <Package size={48} />
          <p>No products in this category</p>
        </div>
      )}
    </div>
  );
}

export default ProductInsightsList;